import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  PRACTICES,
  applyPracticeAccent,
  getPracticeByUuid,
  loadStoredPracticeUuid,
  persistPracticeUuid,
  type Practice,
} from "./practices";
import { setPracticeInUrl, useRoute } from "./router";

interface PracticeContextValue {
  practice: Practice;
  practices: Practice[];
  setPractice: (uuid: string) => void;
}

const PracticeCtx = createContext<PracticeContextValue | null>(null);

export function PracticeProvider({ children }: { children: ReactNode }) {
  const { practiceUuid } = useRoute();

  // Pratique active : URL > localStorage > défaut
  const practice = useMemo(() => {
    const fromUrl = getPracticeByUuid(practiceUuid);
    if (fromUrl) return fromUrl;
    const fromStorage = getPracticeByUuid(loadStoredPracticeUuid());
    return fromStorage ?? PRACTICES[0]!;
  }, [practiceUuid]);

  // Sync : si l'URL n'a pas de cabinet, on injecte celui résolu
  useEffect(() => {
    if (!practiceUuid) {
      setPracticeInUrl(practice.uuid);
    }
  }, [practiceUuid, practice.uuid]);

  // Persiste dans localStorage et applique l'accent à chaque changement
  useEffect(() => {
    persistPracticeUuid(practice.uuid);
    applyPracticeAccent(practice.accent);
  }, [practice.uuid, practice.accent]);

  const setPractice = useCallback((uuid: string) => {
    if (!getPracticeByUuid(uuid)) return;
    setPracticeInUrl(uuid);
  }, []);

  const value = useMemo(
    () => ({ practice, practices: PRACTICES, setPractice }),
    [practice, setPractice],
  );

  return <PracticeCtx.Provider value={value}>{children}</PracticeCtx.Provider>;
}

export function usePractice(): PracticeContextValue {
  const ctx = useContext(PracticeCtx);
  if (!ctx) throw new Error("usePractice must be used within PracticeProvider");
  return ctx;
}
